import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { restoreHostChrome, suppressHostChrome } from '../engine/hostChrome';
import { DEMO_OPTIONS, type DemoOption } from '../sandboxLinks';

export interface DemoBookingDialogProps {
  onClose(): void;
}

export function DemoBookingDialog({ onClose }: DemoBookingDialogProps) {
  const [chosen, setChosen] = useState<DemoOption | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const hidden = suppressHostChrome();

    const returnFocusTo = document.activeElement as HTMLElement | null;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);

    dialogRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      restoreHostChrome(hidden);
      returnFocusTo?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div
      className="tour-demo-backdrop"
      data-testid="demo-booking-dialog"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="tour-demo-dialog"
        data-view={chosen ? 'booking' : 'choose'}
        role="dialog"
        aria-modal="true"
        aria-labelledby="tour-demo-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <header className="tour-demo-head">
          <div>
            <h2 className="tour-demo-title" id="tour-demo-title">
              {chosen ? `Book a demo — ${chosen.label}` : 'Book a live demo'}
            </h2>
            <p className="tour-demo-subtitle">
              {chosen
                ? 'Pick a slot that suits you. You will get a confirmation by email.'
                : 'Which language would you like the demo in?'}
            </p>
          </div>
          <button
            type="button"
            className="tour-demo-close"
            onClick={onClose}
            aria-label="Close"
            data-testid="demo-booking-close"
          >
            ×
          </button>
        </header>

        {!chosen ? (
          <div className="tour-demo-options">
            {DEMO_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className="tour-demo-option"
                onClick={() => setChosen(option)}
                data-testid={`demo-option-${option.id}`}
              >
                <span className="tour-demo-option__label">{option.label}</span>
                <span className="tour-demo-option__desc">{option.description}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="tour-demo-frame-wrap">
            <iframe
              className="tour-demo-frame"
              src={chosen.bookingUrl}
              title={`Book a demo in ${chosen.label}`}
              data-testid="demo-booking-frame"
            />
          </div>
        )}

        <footer className="tour-demo-foot">
          {chosen ? (
            <button
              type="button"
              className="tour-demo-back"
              onClick={() => setChosen(null)}
              data-testid="demo-booking-back"
            >
              ‹ Change language
            </button>
          ) : (
            <span className="tour-demo-note">Takes about 20 minutes.</span>
          )}
          {chosen && (
            <a
              className="tour-demo-newtab"
              href={chosen.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="demo-booking-newtab"
            >
              Open in a new tab ↗
            </a>
          )}
        </footer>
      </div>
    </div>,
    document.body,
  );
}
